import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { ChangeLeadsStatusRequest, LeadsGate } from '../../../../../backend/src/core/leads/leads.gate';
import { ResourceName } from '../../../../../backend/src/core/resource-name';
import { LambdaService } from '../../shared/core/lambda.service';
import { BoardHolderService } from '../board/board.holder.service';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { OpenaiModel } from '../openai/openai';
import { OpenaiService } from '../openai/openai.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { Product } from '../product/product';
import { getInviteMessagePrompt } from '../prompt/leads/invite-message.prompt';
import { getSecondMessagePrompt } from '../prompt/leads/second-message.prompt';
import { Resume } from '../resume/resume';
import { WorkspaceHolderService } from '../workspace/workspace.holder.service';
import { Leads, LeadsStatus } from './leads';

@Injectable({
  providedIn: 'root',
})
export class LeadsService {
  private _currentChangeCount = 0;

  private _changeSubject = new BehaviorSubject<number>(0);

  changed$ = this._changeSubject.asObservable();

  constructor(
    private workspaceHolderService: WorkspaceHolderService,
    private coreItemService: CoreItemService,
    private lambdaService: LambdaService,
    private openaiService: OpenaiService,
    private boardHolderService: BoardHolderService
  ) {}

  change() {
    this._changeSubject.next(this._currentChangeCount++);
  }

  getItemById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as Leads));
  }

  itemList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService
          .getCoreItemList$({
            coreTypeList: [CoreItemType.leads],
            dateRange: {
              startTime: DateTime.utc().minus({ weeks: 4 }),
              endTime: DateTime.utc(),
            },
          })
          .pipe(
            map((list) => {
              list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
              return list as Leads[];
            })
          );
      })
    );
  }

  myItemList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        const board = this.boardHolderService.myPersonalBoard()!;
        return this.coreItemService
          .getCoreItemListByBoard$(board.id, {
            coreTypeList: [CoreItemType.leads],
          })
          .pipe(
            map((list) => {
              list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
              return list as Leads[];
            })
          );
      })
    );
  }

  update$(leads: Leads) {
    posthog.capture(PostHogEvent.leadsUpdate);
    return this.coreItemService.updateCoreItem$(leads).pipe(
      tap((_) => {
        this.change();
      })
    );
  }

  changeLeadsStatus$(leads: Leads, status: LeadsStatus) {
    const request: ChangeLeadsStatusRequest = {
      id: leads.id,
      workspaceId: this.workspaceHolderService.currentWorkspace().id,
      status,
    };

    return this.lambdaService.run$(ResourceName.leads, LeadsGate.actions.changeLeadsStatus, request).pipe(
      tap((data) => {
        this.coreItemService.updateCoreItemInCache(data);
        this.change();
      })
    );
  }

  generateSecondMessage$(resume: Resume, product: Product) {
    const personalBoard = this.boardHolderService.myPersonalBoard()!;

    return this.openaiService.getAnswer$({
      messageList: [
        {
          role: 'user',
          content: getSecondMessagePrompt(
            resume,
            product,
            personalBoard.userBoardConfig?.linkedInSecondMessageInstruction ?? ''
          ),
        },
      ],
      config: { openaiModel: OpenaiModel.o1Mini },
    });
  }

  generateInviteMessage$(resume: Resume, product: Product) {
    const personalBoard = this.boardHolderService.myPersonalBoard()!;

    return this.openaiService.getAnswer$({
      messageList: [
        {
          role: 'user',
          content: getInviteMessagePrompt(
            resume,
            product,
            personalBoard.userBoardConfig?.linkedInInvitationMessageInstruction ?? '',
            personalBoard.userBoardConfig?.linkedInMessageCharsLimit ?? 200
          ),
        },
      ],
      config: { openaiModel: OpenaiModel.o1Mini },
    });
  }

  addItem$(resume: Resume, product: Product) {
    posthog.capture(PostHogEvent.leadsAdd);

    return this.generateInviteMessage$(resume, product).pipe(
      mergeMap((inviteMessage) => {
        const unsafeSubData = {
          productId: product.id,
          resumeId: resume.id,

          inviteMessage,
        };

        return this.coreItemService
          .batchWriteMetadataAndRelationList$({
            addMetadataList: [
              {
                parentId: resume.subData?.linkedInProviderId,
                status: LeadsStatus.linkedInInviteDraft,
                coreType: CoreItemType.leads,
                unsafeSubData,
              },
            ],
          })
          .pipe(
            map((arr) => {
              this.change();
              return arr;
            })
          );
      })
    );
  }

  deleteItemList$(itemList: Leads[]) {
    const deleteMetadataList = itemList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    posthog.capture(PostHogEvent.leadsDelete);

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this.change();
        })
      );
  }
}
